import {
  type StoreCard,
  type BentoBoxData,
  type SemanticBackgroundColor,
} from '@/features/cms/components/CMS/types'
import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'
import { Box } from '@shipt/design-system-layouts'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { CMSShelfLayoutContainerConditional } from '@/features/cms/components/CMS/components/Shelves/CMSShelfLayoutContainer'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { useCMSLayoutContext } from '@/features/cms/components/CMS/CMSLayoutContext'
import { NextLink } from '@/features/shared/elements/Link'
import { BentoBoxHeader } from '@/features/cms/components/CMS/components/BentoBoxHeader'
import { useMedia } from '@/features/shared/hooks/useMedia'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useMemo } from 'react'
import { PromoLockup } from '@/features/cms/components/CMS/components/PromoLockup'
import { screenSizes } from '@shipt/design-system-themes'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { useGlobalHomeSelectStore } from '@/features/shop/components/GlobalHomePage/useGlobalHomeSelectStore'
import { type GuestStore } from '@/features/shop/services/ShoppingStore/types'
import { useDeriveColorProps } from '@/features/cms/components/CMS/hooks/useDeriveColorProps'
import { getRetailerBaseUrl } from '@shared/constants/routes'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { theme } from '@/features/shared/constants/shiptTheme'

type Shape = '1x1' | '1x2' | '2x1'
const sequence: Shape[] = ['2x1', '2x1', '1x2', '1x2', '1x1']
type BentoImage = {
  src: string
  alt: string
  height: number
  width: number
}
const pickImageByRenderedShape = (
  rendered: Shape,
  imgs: {
    small_square_image?: BentoImage
    square_image?: BentoImage
    tall_image?: BentoImage
  },
) => {
  const { small_square_image, square_image, tall_image } = imgs
  if (rendered === '1x1') return small_square_image ?? square_image ?? tall_image
  if (rendered === '2x1') return square_image ?? small_square_image ?? tall_image
  return tall_image ?? small_square_image ?? square_image
}
const getRenderedShape = (
  declared: Shape,
  index: number,
  layout: 'vertical' | 'horizontal',
  isXsScreen: boolean,
  isAtLeast684: boolean,
  totalCards: number,
): Shape => {
  let shape: Shape = declared

  if (isXsScreen) {
    if (layout === 'vertical') {
      if (declared === '2x1') shape = '1x1'
    } else {
      if (declared === '2x1' && index > 0) shape = '1x1'
      if (declared === '1x2') shape = '1x1'
    }
  }

  if (isAtLeast684 && totalCards === 5 && index === 3 && declared === '1x2') {
    shape = '1x1'
  }

  return shape
}

type Props = BentoBoxData & {
  store?: StoreCard['data']
  collectionBackgroundColor?: SemanticBackgroundColor
}

/**
 * This component corresponds to the `bento_box` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/bento_box
 * - **Production** - https://cms.shipt.com/content-type/pool/bento_box
 *
 * Note this component is also referenced within bento_box_collection
 */
export const BentoBox = ({ store, collectionBackgroundColor, ...props }: Props) => {
  const { content_type_id, id, data } = props
  const cmsLayout = useCMSLayoutContext()
  const requestedBackgroundColor = collectionBackgroundColor ?? data.background_color
  const { color: backgroundColor } = useDeriveColorProps(
    requestedBackgroundColor ?? 'neutral-subtle',
  )

  const { layout, cards, header } = data
  const userStoreName = useUserStoreName()
  const storeName = store?.name ?? userStoreName
  const renderHeader = useMemo(() => {
    if (header.content_type_id === 'shelf_header') {
      const { heading, subheading, link } = header.data
      const headingLinkUrl = getRetailerBaseUrl(storeName, link?.url)
      return <BentoBoxHeader heading={heading} subheading={subheading} linkUrl={headingLinkUrl} />
    }
    return null
  }, [header, storeName])

  const isXsScreen = useMedia('(max-width: 479px)', true)
  const isAtLeast684 = useMedia('(min-width: 684px)', false)

  const totalCards = cards?.length ?? 0
  const BentoBoxShelfWrapper =
    header.content_type_id === 'shelf_header' ? BentoBoxAsShelfWrapper : BentoBoxAsContentWrapper

  const wrapperProps =
    header.content_type_id === 'shelf_header'
      ? { ...getCMSWrapperProps({ content_type_id, id }), layout: cmsLayout }
      : {}

  const { trackCMSElementViewed, trackCMSPromoCardViewed, trackCMSPromoCardClicked } =
    useCMSTrackingEvents()

  const { selectStore } = useGlobalHomeSelectStore()
  try {
    return (
      <ScrollIntoView
        onInView={() => {
          trackCMSElementViewed({
            content: 'bento_box',
            type: 'shelf',
            display_format: DISPLAY_FORMAT.BENTO_PROMO_GRID,
          })
        }}
      >
        <BentoBoxShelfWrapper {...wrapperProps}>
          {renderHeader}
          <BentoBoxLayout data-layout={layout} backgroundColor={backgroundColor}>
            <BentoBoxContainer>
              {cards?.map((card, i) => {
                const { square_image, lockup, subheading, tall_image, small_square_image, link } =
                  card.data
                const promoObject = card.metadata?.read_operation_info?.source_object
                const promotionId = promoObject?.object?.promotion_id
                const declaredShape = sequence[i] ?? '2x1'
                const renderedShape = getRenderedShape(
                  declaredShape,
                  i,
                  layout,
                  isXsScreen,
                  isAtLeast684,
                  totalCards,
                )
                const displayFormat = () => {
                  switch (renderedShape) {
                    case '1x1':
                      return DISPLAY_FORMAT.BENTO_COMPACT
                    case '1x2':
                      return DISPLAY_FORMAT.BENTO_HORIZONTAL
                    case '2x1':
                      return DISPLAY_FORMAT.BENTO_VERTICAL
                  }
                }
                const eventProperties = {
                  display_format: displayFormat(),
                  list_index: i,
                  promotion_type_name: promoObject?.type ?? '',
                  promotion_name: card.metadata?.read_operation_info?.source ?? '',
                  promotion_id: promotionId,
                  content: card.analytic_slug,
                }
                const imageSource = pickImageByRenderedShape(renderedShape, {
                  small_square_image,
                  square_image,
                  tall_image,
                })

                const normalizedUrl = getRetailerBaseUrl(storeName, link.url) ?? ''
                const handleClick = (e: React.MouseEvent) => {
                  trackCMSPromoCardClicked(eventProperties)
                  if (store) {
                    e.preventDefault()
                    const guestStore: GuestStore = {
                      id: store.retailer_id,
                      name: store.name,
                      is_pickup: !!store.pickup_eligible,
                      store_location_id: store.store_location_id ?? 0,
                      metro_id: store.metro_id ?? 0,
                    }
                    selectStore(guestStore, normalizedUrl)
                  }
                }

                return (
                  <CardWrapper
                    {...getCMSWrapperProps({
                      id: card.id,
                      content_type_id: card.content_type_id,
                    })}
                    as={NextLink}
                    href={normalizedUrl}
                    className="card"
                    data-cardlayout={declaredShape}
                    onClick={handleClick}
                    key={card.id}
                  >
                    <ScrollIntoViewWrapper
                      onInView={() => trackCMSPromoCardViewed(eventProperties)}
                    >
                      <Box padding={['md']} className="content">
                        <PromoLockup lockup={lockup} />
                        <Body
                          size={{ base: 'sm', md: 'lg' }}
                          variant="secondary"
                          lineHeight="compact"
                        >
                          {subheading}
                        </Body>
                      </Box>
                      {!!imageSource && (
                        <Box className="imageContainer">
                          <NextImage
                            src={imageSource.src}
                            alt={imageSource.alt}
                            width={imageSource.width}
                            height={imageSource.height}
                            sizes="(max-width: 479px) 50vw, (max-width: 767px) 33vw, 25vw"
                          />
                        </Box>
                      )}
                    </ScrollIntoViewWrapper>
                  </CardWrapper>
                )
              })}
            </BentoBoxContainer>
          </BentoBoxLayout>
        </BentoBoxShelfWrapper>
      </ScrollIntoView>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}
const CardWrapper = styled.div`
  display: grid;
  background-color: ${theme.color.background.base.neutral.subtle_00};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  position: relative;
`
const ScrollIntoViewWrapper = styled(ScrollIntoView)`
  display: grid;
  grid-template-rows: inherit;
  grid-template-columns: inherit;
  grid-column: inherit;
`
const BentoBoxAsShelfWrapper = styled(CMSShelfLayoutContainerConditional)`
  margin: 1.5rem 0;
`
const BentoBoxAsContentWrapper = styled.div`
  margin-top: 0.75rem;
  margin-bottom: 2rem;

  @media ${screenSizes.max_mobile} {
    padding-left: 0.75rem;
    padding-right: 1.25rem;
  }
`
const NextImage = styled(Image)`
  display: block;
`
const square = css`
  grid-template-rows: minmax(64px, 1fr) auto;
  grid-template-columns: unset;
  grid-row: unset;
  grid-column: unset;

  .imageContainer {
    position: relative;
    grid-row: 1;
  }

  img {
    box-sizing: border-box;
    position: absolute;
    height: 100px;
    right: 0;
    width: auto;
    padding: 0.5rem 0.5rem 0 0;
    object-fit: contain;
  }
`

const landscape = css`
  grid-template-columns: 50% 50%;
  grid-template-rows: unset;
  grid-column: span 2;

  .content {
    align-self: end;
  }

  .imageContainer {
    position: relative;
    grid-row: unset;
  }

  img {
    position: absolute;
    height: 100%;
    width: auto;
    right: 0;
    object-fit: cover;
    object-position: center left;
  }
`

const portrait = css`
  grid-row: span 2;
  grid-template-rows: 1fr auto;

  .content {
    align-self: end;
  }

  .imageContainer {
    grid-row: 1;
    position: relative;
  }

  img {
    width: 100%;
    height: auto;
  }
`

const BentoBoxLayout = styled.div<{ backgroundColor?: string }>`
  --columns: 2;
  background-color: ${({ backgroundColor }) => backgroundColor};
  box-sizing: border-box;
  padding: 0.75rem;
  border-radius: 16px;
  min-width: 133px;
  border: 1px solid #0000001a;

  [data-cardlayout='2x1'] {
    ${landscape}
  }

  [data-cardlayout='1x2'] {
    ${portrait}
  }

  [data-cardlayout='1x1'] {
    ${square}
  }

  @media ${screenSizes.max_smMobile} {
    [data-cardlayout='2x1']:not(:first-child),
    &[data-layout='vertical'] [data-cardlayout='2x1'] {
      ${square}
    }

    &[data-layout='horizontal'] [data-cardlayout='1x2'] {
      ${square}
    }
  }

  @media ${screenSizes.lgMobile} {
    --columns: 3;
  }

  @media ${screenSizes.tablet} {
    --columns: 4;

    .card:nth-child(4):nth-last-child(2)[data-cardlayout='1x2'] {
      ${square}
    }
  }

  @media ${screenSizes.max_mobile} {
    .card:nth-child(1n + 4) {
      display: none;
    }
  }
`

const BentoBoxContainer = styled.div`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: repeat(var(--columns), 1fr);
  grid-template-rows: repeat(2, minmax(8rem, 1fr));
  grid-auto-flow: column dense;
  gap: 12px;
`
